import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, CloudCheck, CloudAlert, RefreshCw, Clock, Archive, MoreHorizontal, ArrowLeft, CalendarDays, ClipboardList, AlertTriangle, Bell, FlaskConical, CalendarClock, Megaphone, ArrowRightLeft, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSync } from "@/context/SyncContext";
import { AppHeader } from "@/components/AppHeader";
import { RegistroSuggeritoDayflow } from "@/components/worker/RegistroSuggeritoDayflow";
import { AreaCollapseToggle } from "@/components/worker/AreaCollapseToggle";
import { ImportantAlerts } from "@/components/worker/ImportantAlerts";
import { CommunicationDetailDialog } from "@/components/worker/CommunicationDetail";
import { PushBridge } from "@/components/worker/PushBridge";
import { IntroCover } from "@/components/IntroCover";
import { peekIntro, clearIntro } from "@/lib/introFlag";
import { welcomeToast } from "@/lib/welcome";
import { Card, CardContent } from "@/components/ui/card";
import { SERVICES, slugFromKey, fmtDateIt } from "@/lib/services";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { stampTime } from "@/components/StampInfo";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ROW_ACCENTS = ["border-l-[#22364D]", "border-l-[#22364D]", "border-l-[#22364D]", "border-l-[#22364D]"];

function BachecaSostituzioni() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [busy, setBusy] = useState("");
  const [conflict, setConflict] = useState(null); // {id, conflicts:[...]}

  const load = () => api.get("/my/substitutions").then((r) => { setItems(r.data.substitutions || []); setCount(r.data.open_count || 0); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const fmtDate = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "long" }); } catch { return iso; } };
  const accept = async (id, force = false) => {
    setBusy(id);
    try {
      await api.post(`/my/substitutions/${id}/accept${force ? "?force=true" : ""}`);
      toast.success("Sostituzione presa in carico", { description: "Aggiunta alla tua Agenda settimanale." });
      setConflict(null);
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det && typeof det === "object" && det.code === "agenda_conflict") {
        setConflict({ id, conflicts: det.conflicts || [] });
        setBusy(""); return; // popup di conferma, nessun errore
      }
      toast.error(typeof det === "string" ? det : "Impossibile accettare");
    } finally { setBusy(""); await load(); }
  };

  const tone = {
    disponibile: { card: "border-sky-200 bg-sky-50/70", badge: "bg-sky-100 text-sky-700 border-sky-200", label: "Disponibile" },
    urgente: { card: "border-orange-300 bg-orange-50/80", badge: "bg-orange-100 text-orange-700 border-orange-300", label: "Urgente" },
    coperta: { card: "border-emerald-200 bg-emerald-50/70", badge: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Coperta" },
  };
  const Field = ({ label, value }) => value ? <p className="text-xs text-slate-600"><span className="text-slate-400">{label}:</span> {value}</p> : null;

  return (
    <section className="orbit-area-title mt-6" data-testid="area-sostituzioni">
      <h2 className="font-heading text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2"><span className="inline-block h-4 w-1.5 rounded-full bg-[#C6A3B4]" />Sostituzioni</h2>
      <button data-testid="bacheca-sostituzioni-btn" onClick={() => { setOpen(true); load(); }}
        className={`relative w-full rounded-xl border shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.99] p-4 flex items-center gap-3 ${count > 0 ? "bg-[#F0E1E7] border-[#E1CFD8]" : "bg-white border-[#A9C7DD]"}`}>
        <div className={`h-10 w-10 rounded-lg border flex items-center justify-center shrink-0 ${count > 0 ? "bg-white/70 border-[#E1CFD8]" : "bg-[#EDF5FA] border-[#C4D8E6]"}`}><ArrowRightLeft className={`h-5 w-5 ${count > 0 ? "text-[#4A3340]" : "text-[#22364D]"}`} /></div>
        <div className="text-left flex-1"><p className="font-semibold text-slate-800">Bacheca Sostituzioni</p><p className="text-xs text-slate-500">Prestazioni da coprire negli appalti a cui appartieni</p></div>
        {count > 0 && <span data-testid="bacheca-badge" className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-orange-500 text-white text-xs font-bold inline-flex items-center justify-center">{count}</span>}
        <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="bacheca-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ArrowRightLeft className="h-5 w-5 text-[#4A3340]" /> Bacheca Sostituzioni</DialogTitle></DialogHeader>
          {items.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Nessuna sostituzione attiva al momento.</p>}
          <div className="space-y-3">
            {items.map((s) => {
              const t = tone[s.ui_status] || tone.disponibile;
              return (
                <div key={s.id} data-testid={`sub-card-${s.id}`} className={`rounded-xl border p-3.5 ${t.card}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md border ${t.badge}`}>{t.label}</span>
                    <span className="text-xs font-semibold text-slate-500">{SERVICES[slugFromKey(s.service)]?.label || s.service}</span>
                  </div>
                  <p className="text-base font-bold text-slate-900 capitalize leading-tight">{fmtDate(s.date)}</p>
                  <p className="text-lg font-extrabold text-slate-900 flex items-center gap-1.5"><Clock className="h-4 w-4 text-slate-400" />{s.start_time}–{s.end_time}</p>
                  <div className="mt-2 space-y-0.5">
                    <Field label="Da sostituire" value={s.absent_worker_label} />
                    {s.entity_kind !== "appalto" && <Field label="Destinazione" value={s.entity_label} />}
                    <Field label="Ruolo" value={s.role} />
                    <Field label="Nota" value={s.note} />
                  </div>
                  {s.ui_status === "coperta" ? (
                    <div className="mt-2.5 flex items-center gap-1.5 text-sm text-emerald-800">
                      <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
                      <span><b>{s.substitute_worker_label}</b> ha coperto questa sostituzione</span>
                    </div>
                  ) : (
                    <div className="mt-3 flex justify-end">
                      <Button size="sm" data-testid={`sub-accept-${s.id}`} disabled={busy === s.id} onClick={() => accept(s.id)}
                        className="rounded-lg bg-[#22364D] hover:bg-[#2f4a6b] text-white">
                        {busy === s.id ? "…" : "Accetta sostituzione"}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!conflict} onOpenChange={(o) => !o && setConflict(null)}>
        <DialogContent className="rounded-2xl" data-testid="sub-conflict-dialog">
          <DialogHeader><DialogTitle className="font-heading">Conflitto con l'Agenda settimanale</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">L'Agenda settimanale indica che questa sostituzione va in conflitto con uno dei tuoi impegni. Vuoi procedere comunque?</p>
          {conflict?.conflicts?.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 space-y-0.5">
              {conflict.conflicts.map((c, i) => (
                <p key={i} className="text-xs text-amber-800">{SERVICES[slugFromKey(c.service_type)]?.label || c.service_type} · {c.start_time}–{c.end_time}</p>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setConflict(null)} className="rounded-lg" data-testid="sub-conflict-no">No</Button>
            <Button onClick={() => accept(conflict.id, true)} disabled={busy === conflict?.id} data-testid="sub-conflict-yes"
              className="rounded-lg bg-[#22364D] hover:bg-[#2f4a6b] text-white">Sì, procedi</Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function CommDeepLink() {
  const [params, setParams] = useSearchParams();
  const [detail, setDetail] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const openComm = async (id) => {
    if (!id) return;
    try { const { data } = await api.get(`/my/communications/${id}`); setDetail(data); }
    catch { toast.error("Comunicazione non trovata"); }
  };
  useEffect(() => {
    const cid = params.get("comm");
    if (cid) { openComm(cid); const p = new URLSearchParams(params); p.delete("comm"); setParams(p, { replace: true }); }
    const h = (e) => openComm(e.detail?.id);
    window.addEventListener("orbit:open-comm", h);
    return () => window.removeEventListener("orbit:open-comm", h);
    // eslint-disable-next-line
  }, []);
  const confirmRead = async () => {
    if (!detail?.comm_id) return;
    setConfirming(true);
    try {
      await api.post(`/my/communications/${detail.comm_id}/read`);
      toast.success("Comunicazione archiviata");
      setDetail(null);
      window.dispatchEvent(new CustomEvent("orbit:comm-archived"));
    } catch { toast.error("Impossibile confermare la lettura"); }
    finally { setConfirming(false); }
  };
  return (
    <CommunicationDetailDialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}
      comm={detail} onConfirm={detail && !detail.read_at ? confirmRead : undefined} confirming={confirming} />
  );
}

function ArchivioComunicazioniCard() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null);
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return iso; } };

  const load = () => api.get("/my/communications/archive").then((r) => setItems(r.data.communications || [])).catch(() => setItems([]));
  useEffect(() => {
    const h = () => { if (open) load(); };
    window.addEventListener("orbit:comm-archived", h);
    return () => window.removeEventListener("orbit:comm-archived", h);
  }, [open]);
  const openCard = () => { setOpen(true); load(); };
  const toDetail = (c) => setDetail({ context: `Ufficio${c.office_actor_name ? " · " + c.office_actor_name : ""}`, datetime: c.sent_at, body: c.text, read_at: c.read_at });

  return (
    <>
      <button data-testid="comm-archive-btn" onClick={openCard}
        className="mt-3 w-full rounded-xl bg-white border border-[#A9C7DD] shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.99] p-4 flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-[#EDF5FA] border border-[#C4D8E6] flex items-center justify-center"><Megaphone className="h-5 w-5 text-[#22364D]" /></div>
        <div className="text-left flex-1"><p className="font-semibold text-slate-800">Archivio comunicazioni</p><p className="text-xs text-slate-500">Le comunicazioni dell'Ufficio che hai già letto</p></div>
        <ChevronRight className="h-5 w-5 text-slate-400" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="comm-archive-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Megaphone className="h-5 w-5 text-[#22364D]" /> Archivio comunicazioni</DialogTitle>
          </DialogHeader>
          {items === null && <p className="text-sm text-slate-400 py-4">Caricamento…</p>}
          {items !== null && items.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center" data-testid="comm-archive-placeholder">
              <p className="text-sm font-semibold text-slate-600">Nessuna comunicazione archiviata</p>
              <p className="text-xs text-slate-400 mt-1">Qui troverai le comunicazioni che contrassegnerai come lette dallo spazio "Avvisi importanti".</p>
            </div>
          )}
          {items !== null && items.length > 0 && (
            <div className="space-y-2" data-testid="comm-archive-list">
              {items.map((c) => (
                <button key={c.id} data-testid={`archive-comm-${c.id}`} onClick={() => toDetail(c)}
                  className="w-full text-left rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className="text-xs font-semibold text-slate-500">{fmtDate(c.sent_at)} · Ufficio{c.office_actor_name ? ` · ${c.office_actor_name}` : ""}</span>
                    {c.read_at && <span className="text-[11px] text-emerald-600 font-semibold shrink-0">Letto</span>}
                  </div>
                  <p className="text-sm text-slate-800 line-clamp-2 whitespace-pre-wrap">{c.text}</p>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <CommunicationDetailDialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }} comm={detail} />
    </>
  );
}

function SyncBar() {
  const { indicator, pendingCount, errorCount, lastSync } = useSync();
  // Il messaggio positivo "sincronizzato" sparisce con fade dopo 5s (solo timeout locale, nessun polling).
  const [fading, setFading] = useState(false);
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    if (indicator !== "synced") { setFading(false); setHidden(false); return; }
    setFading(false); setHidden(false);
    const t1 = setTimeout(() => setFading(true), 5000);
    const t2 = setTimeout(() => setHidden(true), 5700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [indicator]);
  const map = {
    synced: { icon: CloudCheck, text: "Tutte le registrazioni sono sincronizzate", cls: "bg-success/5 border-success/20 text-success" },
    pending: { icon: CloudAlert, text: `${pendingCount} ${pendingCount === 1 ? "registrazione da sincronizzare" : "registrazioni da sincronizzare"}`, cls: "bg-amber-50 border-amber-200 text-amber-700" },
    syncing: { icon: RefreshCw, text: "Sincronizzazione in corso…", cls: "bg-[#E0F2FE] border-primary/20 text-primary" },
    error: { icon: CloudAlert, text: `Errore di sincronizzazione${errorCount ? ` (${errorCount})` : ""}`, cls: "bg-destructive/5 border-destructive/20 text-destructive" },
  };
  const s = map[indicator];
  const Icon = s.icon;
  if (indicator === "synced" && hidden) return null;
  return (
    <div data-testid="sync-status" className={`mt-5 flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm border transition-opacity duration-700 ${fading ? "opacity-0" : "opacity-100"} ${s.cls}`}>
      <Icon className={`h-5 w-5 ${indicator === "syncing" ? "animate-spin" : ""}`} />
      <span className="font-medium">{s.text}</span>
      {lastSync && (
        <span className="ml-auto flex items-center gap-1 text-xs text-slate-400">
          <Clock className="h-3.5 w-3.5" />
          {new Date(lastSync).toLocaleString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
        </span>
      )}
    </div>
  );
}

export default function WorkerHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [intro, setIntro] = useState(() => peekIntro());
  useEffect(() => { clearIntro(); }, []);
  const [recents, setRecents] = useState(() => {
    try { return JSON.parse(localStorage.getItem("orbit_recents") || "[]"); } catch { return []; }
  });
  const [active, setActive] = useState(null);
  const [enabledKeys, setEnabledKeys] = useState(null);
  const [usage, setUsage] = useState([]);
  const [showOther, setShowOther] = useState(false);
  const [leaveUpdates, setLeaveUpdates] = useState(0);
  const [errCount, setErrCount] = useState(null);
  const [errWarn, setErrWarn] = useState(false);
  const [errNotice, setErrNotice] = useState(false);
  const [errAlert, setErrAlert] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [demoOn, setDemoOn] = useState(!!user?.demo_data_on);
  const [demoBusy, setDemoBusy] = useState(false);

  // Aree comprimibili Home (preferenza per-account). Default: tutte aperte.
  const [collapsed, setCollapsed] = useState({ agenda: false, registra: false, frequenti: false });
  useEffect(() => {
    api.get("/me/home-collapsed").then((r) => {
      const arr = r.data?.collapsed || [];
      setCollapsed({ agenda: arr.includes("agenda"), registra: arr.includes("registra"), frequenti: arr.includes("frequenti") });
    }).catch(() => {});
  }, []);
  const toggleArea = (key) => {
    setCollapsed((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      const list = Object.keys(next).filter((k) => next[k]);
      api.put("/me/home-collapsed", { collapsed: list }).catch(() => {});
      return next;
    });
  };

  useEffect(() => {
    if (!user?.is_demo) return;
    api.get("/demo/data-status").then((r) => setDemoOn(!!r.data?.enabled)).catch(() => {});
  }, [user?.is_demo]);

  const toggleDemo = async (next) => {
    if (demoBusy) return;
    setDemoBusy(true);
    try {
      if (next) {
        const { data } = await api.post("/demo/data/on");
        setDemoOn(true);
        const g = data?.generated || {};
        toast.success(`Dati demo attivati (${g.work_entries || 0} prestazioni). Aggiorno la vista…`);
      } else {
        await api.post("/demo/data/off");
        setDemoOn(false);
        toast.success("Dati demo rimossi. Aggiorno la vista…");
      }
      setTimeout(() => window.location.reload(), 700);
    } catch (e) {
      toast.error("Operazione non riuscita");
      setDemoBusy(false);
    }
  };

  useEffect(() => {
    api.get("/recent-registers").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setRecents(list);
      localStorage.setItem("orbit_recents", JSON.stringify(list));
    }).catch(() => {});
    api.get("/service/active").then((r) => setActive(r.data.active)).catch(() => {});
    api.get("/my-services").then((r) => {
      setEnabledKeys(new Set((r.data || []).map((s) => s.slug)));
    }).catch(() => setEnabledKeys(new Set()));
    api.get("/my-service-usage").then((r) => setUsage(r.data || [])).catch(() => setUsage([]));
    api.get("/leave/updates-count").then((r) => setLeaveUpdates(r.data?.count || 0)).catch(() => {});
    api.get("/my/notifications").then((r) => setNotifs(r.data || [])).catch(() => {});
    api.get("/my/anomaly-count").then((r) => {
      setErrCount(r.data?.count ?? 0);
      setErrWarn(!!r.data?.warn);
      if (r.data?.changed) {
        setErrNotice(true);
        setTimeout(() => setErrNotice(false), 6000);
      }
      if (r.data?.alert?.show) setErrAlert(r.data.alert);
    }).catch(() => {});
  }, []);

  // Servizi abilitati nell'ordine di default del catalogo (fallback senza storico).
  const availableServices = Object.entries(SERVICES).filter(
    ([, cfg]) => enabledKeys && enabledKeys.has(cfg.key)
  );

  // Classifica personale per frequenza (dalle work_entries): count desc, poi più recente.
  // A parità o senza storico, si mantiene l'ordine di default (indice originale).
  const usageRank = new Map(usage.map((u, i) => [u.service_type, i]));
  const orderedServices = availableServices
    .map((entry, idx) => ({ entry, idx }))
    .sort((a, b) => {
      const ra = usageRank.has(a.entry[1].key) ? usageRank.get(a.entry[1].key) : Infinity;
      const rb = usageRank.has(b.entry[1].key) ? usageRank.get(b.entry[1].key) : Infinity;
      if (ra !== rb) return ra - rb;
      return a.idx - b.idx;
    })
    .map((x) => x.entry);

  const useOverflow = orderedServices.length > 4;
  const primaryServices = useOverflow ? orderedServices.slice(0, 4) : orderedServices;
  const otherServices = useOverflow ? orderedServices.slice(4) : [];

  const goActive = () => {
    if (!active) return;
    let url = `/registro/${slugFromKey(active.service_type)}/${active.entity?.id}`;
    const qp = [];
    if (active.entity_kind) qp.push(`kind=${active.entity_kind}`);
    if (active.role_or_task) qp.push(`role=${encodeURIComponent(active.role_or_task)}`);
    if (qp.length) url += `?${qp.join("&")}`;
    navigate(url);
  };

  const renderServiceTile = (slug, cfg, i) => {
    const Icon = cfg.icon;
    return (
      <motion.button key={slug} data-testid={`service-btn-${slug}`}
        initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.05 }}
        onClick={() => (active ? goActive() : navigate(`/servizio/${slug}`))}
        className={`group text-left rounded-2xl bg-white border border-[#DCE3EB] shadow-[0_1px_3px_rgba(34,54,77,0.06)] transition-all duration-200 p-4 ${active ? "opacity-50 cursor-not-allowed" : "hover:shadow-[0_8px_20px_rgba(34,54,77,0.12)] hover:-translate-y-0.5 active:scale-[0.98]"}`}>
        <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] flex items-center justify-center mb-3 shadow-inner">
          <Icon className="h-5 w-5 text-[#22364D]" strokeWidth={2} />
        </div>
        <p className="font-heading font-bold text-slate-800 leading-tight">{cfg.label}</p>
        <span className="mt-2 inline-flex items-center text-xs font-semibold text-primary">Apri <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
      </motion.button>
    );
  };

  return (
    <div className="worker-theme min-h-screen bg-[#EDF0F4] pb-10">
      <PushBridge />
      {intro && <IntroCover onDone={() => { setIntro(false); welcomeToast(user?.nome); }} />}
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 py-6">
        <motion.div initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          <p className="text-sm text-slate-500">Ciao,</p>
          <h1 className={`font-heading font-bold tracking-tight text-slate-900 ${[user?.nome, user?.cognome].filter(Boolean).join(" ").length > 18 ? "text-xl" : "text-2xl"}`}>{[user?.nome, user?.cognome].filter(Boolean).join(" ")}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{user?.ruolo_professionale}</p>
        </motion.div>

        <ImportantAlerts />

        {user?.is_demo && (
          <div data-testid="demo-data-toggle-wrap"
            className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="h-9 w-9 rounded-lg bg-[#E0F2FE] flex items-center justify-center shrink-0">
                <FlaskConical className="h-4.5 w-4.5 text-primary" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">Dati demo</p>
                <p className="text-xs text-slate-500">{demoOn ? "Attivi · dataset di prova del mese corrente" : "Genera prestazioni di prova per testare Orbit"}</p>
              </div>
            </div>
            <Switch checked={demoOn} disabled={demoBusy} onCheckedChange={toggleDemo}
              data-testid="demo-data-toggle" aria-label="Dati demo" />
          </div>
        )}

        <SyncBar />

        <AnimatePresence>
          {errNotice && (
            <motion.p key="err-notice" data-testid="anomaly-count-notice"
              initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
              className="mt-2 text-xs text-slate-500 flex items-center gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> Il conteggio degli errori di timbratura è stato aggiornato.
            </motion.p>
          )}
        </AnimatePresence>

        {errAlert && (
          <div data-testid="anomaly-frequency-alert"
            className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-red-800 text-sm">Frequenti errori di timbratura rilevati</p>
              <p className="text-xs text-red-700 mt-0.5">Si richiede maggiore attenzione nella registrazione delle prestazioni.</p>
              <button data-testid="anomaly-alert-ack-btn"
                onClick={async () => { try { await api.post("/my/anomaly-alert/ack"); } catch {} setErrAlert(null); }}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 bg-white border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-100 transition-colors">
                Ho preso visione
              </button>
            </div>
          </div>
        )}

        {errWarn && (
          <button data-testid="anomaly-count-indicator" onClick={() => navigate("/archivio")}
            className={`mt-2 inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2.5 py-1 transition-colors ${errWarn ? "text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200" : "text-slate-500 bg-slate-50 hover:bg-slate-100 border border-slate-200"}`}>
            <AlertTriangle className="h-3.5 w-3.5" /> Errori di timbratura mensili: <span className="font-bold" data-testid="anomaly-count-value">{errCount}</span>
          </button>
        )}

        {notifs.filter((n) => !n.read_at && !["relation_reminder", "agenda_reminder"].includes(n.type)).length > 0 && (
          <div className="mt-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3" data-testid="worker-notifications">
            <div className="flex items-center justify-between mb-2">
              <p className="font-semibold text-sky-900 text-sm flex items-center gap-1.5"><Bell className="h-4 w-4" /> Aggiornamenti dall'Ufficio</p>
              <button data-testid="notifications-mark-read"
                onClick={async () => { try { await api.post("/my/notifications/read"); } catch {} setNotifs((ns) => ns.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() }))); }}
                className="text-xs font-semibold text-sky-700 hover:underline">Segna come letto</button>
            </div>
            <div className="space-y-2">
              {notifs.filter((n) => !n.read_at && !["relation_reminder", "agenda_reminder"].includes(n.type)).map((n) => (
                <div key={n.id} className="text-xs bg-white rounded-lg border border-sky-100 px-3 py-2" data-testid={`notification-${n.id}`}>
                  <p className="text-slate-800">{n.title}</p>
                  {n.detail && (n.detail.previous || n.detail.new) && (
                    <p className="text-slate-500 mt-1">
                      {n.detail.work_date && <>Prestazione del <b>{n.detail.work_date}</b> · </>}
                      Prima: <span className="line-through">{n.detail.previous}</span> → Ora: <b className="text-slate-700">{n.detail.new}</b>
                    </p>
                  )}
                  {n.detail?.link && (
                    <button data-testid={`notification-link-${n.id}`} onClick={() => navigate(n.detail.link)}
                      className="mt-1.5 inline-flex items-center gap-1 font-semibold text-sky-700 hover:underline">
                      Vai all'Agenda Settimanale <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {active && (
          <button data-testid="active-service-banner" onClick={goActive}
            className="mt-4 w-full rounded-xl bg-emerald-50 border border-emerald-300 shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.99] p-4 flex items-center gap-3 text-left">
            <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0"><Clock className="h-5 w-5 text-emerald-700 animate-pulse" /></div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-emerald-800">Servizio in corso · chiudi per continuare</p>
              <p className="text-xs text-emerald-700 truncate">{active.entity?.titolo}</p>
              <p className="text-xs text-emerald-700 mt-1">
                Entrata dichiarata: <b>{active.start_time || "—"}</b> · Timbratura effettuata alle: <b data-testid="home-active-in-real">{stampTime(active.server_open_at) || "Dato non disponibile"}</b>
              </p>
            </div>
            <ChevronRight className="h-5 w-5 text-emerald-600 shrink-0" />
          </button>
        )}

        <RegistroSuggeritoDayflow reloadKey={active ? active.id : "none"} collapsed={collapsed.agenda} onToggle={() => toggleArea("agenda")} />

        <h2 className="orbit-area-title font-heading text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2"><span className="inline-block h-4 w-1.5 rounded-full bg-[#C6A3B4]" />Registra una prestazione<AreaCollapseToggle testid="toggle-area-registra" collapsed={collapsed.registra} onToggle={() => toggleArea("registra")} /></h2>
        {!collapsed.registra && (<>
        {active && (
          <p data-testid="services-locked-note" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
            Hai un servizio in corso: chiudilo prima di aprirne un altro o compilare altri registri.
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          {enabledKeys === null ? (
            [0, 1, 2, 3].map((i) => (
              <div key={i} data-testid="services-loading-skeleton"
                className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4 animate-pulse h-[132px]">
                <div className="h-11 w-11 rounded-xl bg-slate-100 mb-3" />
                <div className="h-4 w-3/4 rounded bg-slate-100 mb-2" />
                <div className="h-3 w-1/3 rounded bg-slate-100" />
              </div>
            ))
          ) : availableServices.length === 0 ? (
            <Card className="col-span-2 rounded-xl border-slate-200 shadow-sm">
              <CardContent className="p-6 text-center text-slate-500 text-sm" data-testid="no-services-message">
                Nessun servizio abilitato per i tuoi appalti. Contatta l'Ufficio per l'abilitazione.
              </CardContent>
            </Card>
          ) : (
            <>
              {primaryServices.map(([slug, cfg], i) => renderServiceTile(slug, cfg, i))}
              {useOverflow && (
                <motion.button key="altro" data-testid="service-btn-altro"
                  initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 4 * 0.05 }}
                  onClick={() => setShowOther(true)}
                  className="group text-left rounded-2xl bg-white border border-[#DCE3EB] shadow-[0_1px_3px_rgba(34,54,77,0.06)] transition-all duration-200 p-4 hover:shadow-[0_8px_20px_rgba(34,54,77,0.12)] hover:-translate-y-0.5 active:scale-[0.98]">
                  <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#D6E7F3] to-[#A9C7DD] border border-[#93B4CC] flex items-center justify-center mb-3 shadow-inner">
                    <MoreHorizontal className="h-5 w-5 text-[#22364D]" strokeWidth={2} />
                  </div>
                  <p className="font-heading font-bold text-slate-800 leading-tight">Altro</p>
                  <span className="mt-2 inline-flex items-center text-xs font-semibold text-primary">{otherServices.length} servizi <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
                </motion.button>
              )}
            </>
          )}
          {/* Card fissa Ore indirette (ore dichiarative, NON un servizio; fuori dal ranking frequenti). */}
          {enabledKeys !== null && (
            <motion.button key="ore-indirette" data-testid="indirect-hours-card"
              initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 5 * 0.05 }}
              onClick={() => navigate("/ore-indirette")}
              className="group text-left rounded-2xl bg-[#E1E9F1] border border-[#C2D2E1] shadow-[0_1px_3px_rgba(34,54,77,0.06)] transition-all duration-200 p-4 hover:shadow-[0_8px_20px_rgba(34,54,77,0.12)] hover:-translate-y-0.5 active:scale-[0.98]">
              <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#CBDAE8] to-[#93B4CC] border border-[#7FA3BF] flex items-center justify-center mb-3 shadow-inner">
                <ClipboardList className="h-5 w-5 text-[#22364D]" strokeWidth={2} />
              </div>
              <p className="font-heading font-bold text-slate-800 leading-tight">Ore indirette</p>
              <span className="mt-2 inline-flex items-center text-xs font-semibold text-[#22364D]">Dichiara <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" /></span>
            </motion.button>
          )}
        </div>
        </>)}

        <Dialog open={showOther} onOpenChange={setShowOther}>
          <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">Altri servizi</DialogTitle>
            </DialogHeader>
            {active && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Hai un servizio in corso: chiudilo prima di aprirne un altro.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3" data-testid="other-services-grid">
              {otherServices.map(([slug, cfg], i) => renderServiceTile(slug, cfg, i))}
            </div>
          </DialogContent>
        </Dialog>

        <h2 className="orbit-area-title font-heading text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2"><span className="inline-block h-4 w-1.5 rounded-full bg-[#C6A3B4]" />Registri frequenti<AreaCollapseToggle testid="toggle-area-frequenti" collapsed={collapsed.frequenti} onToggle={() => toggleArea("frequenti")} /></h2>
        {!collapsed.frequenti && ((Array.isArray(recents) ? recents : []).length === 0 ? (
          <Card className="rounded-xl border-slate-200 shadow-sm"><CardContent className="p-6 text-center text-slate-500 text-sm">Nessun registro utilizzato di recente.</CardContent></Card>
        ) : (
          <div className="space-y-2" data-testid="recent-registers-list">
            {(Array.isArray(recents) ? recents : []).slice(0, 8).map((r, i) => {
              const cfg = SERVICES[slugFromKey(r.service_type)];
              const Icon = cfg.icon;
              const isCasaR = r.service_type === "casa_di_riposo";
              const displayTitle = isCasaR && r.role_or_task
                ? (r.entity_kind === "operative_site" ? `${r.role_or_task} — ${r.titolo}` : r.role_or_task)
                : r.titolo;
              return (
                <motion.button key={`${r.service_type}-${r.entity_id}-${r.role_or_task || ""}`} data-testid={`recent-card-${i}`}
                  initial={false} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: Math.min(i * 0.035, 0.3) }}
                  onClick={() => {
                    const params = new URLSearchParams();
                    if (r.entity_kind) params.set("kind", r.entity_kind);
                    if (r.role_or_task) params.set("role", r.role_or_task);
                    const qs = params.toString();
                    navigate(`/registro/${slugFromKey(r.service_type)}/${r.entity_id}${qs ? `?${qs}` : ""}`);
                  }}
                  className={`w-full text-left rounded-xl bg-white border border-[#DCE3EB] border-l-[5px] ${ROW_ACCENTS[i % 4]} shadow-[0_1px_2px_rgba(34,54,77,0.05)] hover:shadow-[0_6px_16px_rgba(34,54,77,0.10)] hover:bg-[#FAFCFE] transition-all duration-200 active:scale-[0.99] px-3 py-2.5 flex items-center gap-2.5`}>
                  <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#EDF5FA] to-[#CFE0EC] border border-[#C4D8E6] flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-[#22364D]" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 truncate leading-tight">{displayTitle}
                      <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-primary/70">{cfg.short}</span>
                    </p>
                    <p className="text-xs text-slate-500 truncate leading-tight">{r.sottotitolo}{r.last_entry && ` · ${fmtDateIt(r.last_entry.work_date)} ${r.last_entry.start_time}–${r.last_entry.end_time}`}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
                </motion.button>
              );
            })}
          </div>
        ))}

        <BachecaSostituzioni />

        {/* Area personale: solo spaziatura, nessun riquadro. */}
        <section data-testid="area-personale" className="orbit-area-title">
          <h2 className="font-heading text-lg font-semibold text-slate-800 mb-3 flex items-center gap-2"><span className="inline-block h-4 w-1.5 rounded-full bg-[#C6A3B4]" />Area personale</h2>
          <button data-testid="archive-btn" onClick={() => navigate("/archivio")}
            className="w-full rounded-xl bg-white border border-[#A9C7DD] shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.99] p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#EDF5FA] border border-[#C4D8E6] flex items-center justify-center"><Archive className="h-5 w-5 text-[#22364D]" /></div>
            <div className="text-left flex-1"><p className="font-semibold text-slate-800">I miei Registri</p><p className="text-xs text-slate-500">Mese corrente e mesi precedenti · Registro PDF e Libro Presenze Excel</p></div>
            <ChevronRight className="h-5 w-5 text-slate-400" />
          </button>

          <button data-testid="leave-btn" onClick={() => navigate("/assenze")}
            className="relative mt-3 w-full rounded-xl bg-white border border-[#A9C7DD] shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.99] p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#EDF5FA] border border-[#C4D8E6] flex items-center justify-center"><CalendarDays className="h-5 w-5 text-[#22364D]" /></div>
            <div className="text-left flex-1"><p className="font-semibold text-slate-800">Assenze e permessi</p><p className="text-xs text-slate-500">Richiedi le ferie e consulta lo stato delle richieste</p></div>
            {leaveUpdates > 0 && (
              <span data-testid="leave-updates-badge"
                className="min-w-[24px] h-6 px-1.5 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center shadow-sm">
                {leaveUpdates}
              </span>
            )}
            <ChevronRight className="h-5 w-5 text-slate-400" />
          </button>

          <button data-testid="agenda-btn" onClick={() => navigate("/agenda")}
            className="mt-3 w-full rounded-xl bg-white border border-[#A9C7DD] shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.99] p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-[#EDF5FA] border border-[#C4D8E6] flex items-center justify-center"><CalendarClock className="h-5 w-5 text-[#22364D]" /></div>
            <div className="text-left flex-1"><p className="font-semibold text-slate-800">Agenda Settimanale</p><p className="text-xs text-slate-500">La tua settimana tipo ricorrente di servizi programmati</p></div>
            <ChevronRight className="h-5 w-5 text-slate-400" />
          </button>

          <ArchivioComunicazioniCard />
        </section>
        <CommDeepLink />
      </main>
    </div>
  );
}
